import React, { useState, useEffect } from 'react';
import { Modal, Toast, ToastContainer } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { 
  createDirectSubscription, 
  completeDirectSubscription,
  fetchPaymentMethods,
  fetchSubscriptions
} from '../../store/slices/subscriptionSlice';
import { addToCart } from '../../store/slices/cartSlice';
import WizardStepMetalType from './WizardStepMetalType';
import WizardStepTimePeriod from './WizardStepTimePeriod';
import WizardStepTargetWeight from './WizardStepTargetWeight';
import WizardStepAmount from './WizardStepAmount';
import WizardStepReview from './WizardStepReview';
import WizardStepPaymentMethodSelection from './WizardStepPaymentMethodSelection';
import WizardStepAddress from './WizardStepAddress';
import WizardStepThankYou from './WizardStepThankYou';
import { useApi } from '../../services/api';

const SubscriptionWizard = ({ show, onHide, initialData }) => {
  const [step, setStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    metal_type: '',
    target_weight: 0,
    weight_type: '',
    period: '',
    amount_per_period: 0,
    selected_payment_method_id: null,
    selected_address_id: null
  });

  // Apply initial data from onboarding preset and skip to review step
  useEffect(() => {
    if (initialData && show) {
      setWizardData({
        ...wizardData,
        metal_type: initialData.metal_type || '',
        target_weight: initialData.target_weight || 0,
        weight_type: initialData.weight_type || '',
        period: initialData.period || '',
        amount_per_period: initialData.amount_per_period || 0,
        selected_payment_method_id: null,
        selected_address_id: null
      });
      // Skip to review step (step 5)
      setStep(5);
    }
  }, [initialData, show]);

  // Debug component mount/unmount
  useEffect(() => {
    console.log('SubscriptionWizard MOUNTED');
    return () => {
      console.log('SubscriptionWizard UNMOUNTING');
    };
  }, []);

  // Debug show prop changes
  useEffect(() => {
    console.log('Modal show prop changed to:', show);
    if (!show) {
      console.log('Modal is being hidden, resetting step to 1');
      setStep(1);
    }
  }, [show]);
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  
  const api = useApi();
  const dispatch = useDispatch();
  const paymentIntent = useSelector(state => state.subscriptions?.paymentIntent);
  const subscriptionCreated = useSelector(state => state.subscriptions?.subscriptionCreated);
  const { paymentMethods = [] } = useSelector(state => state.subscriptions || {});

  // Debug step changes
  useEffect(() => {
    console.log('Step state changed to:', step);
  }, [step]);

  // Debug subscriptionCreated changes
  useEffect(() => {
    console.log('subscriptionCreated value changed to:', subscriptionCreated);
  }, [subscriptionCreated]);

  // Fetch payment methods when user reaches payment method selection step
  // DISABLED - This causes the component to unmount
  // useEffect(() => {
  //   if (step === 6 && show) {
  //     console.log('Dispatching fetchPaymentMethods...');
  //     dispatch(fetchPaymentMethods());
  //     console.log('fetchPaymentMethods dispatched');
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [step, show]);

  // Handle payment intent creation response
  useEffect(() => {
    if (paymentIntent && paymentIntent.client_secret) {
      setClientSecret(paymentIntent.client_secret);
    }
  }, [paymentIntent]);

  // DISABLED - We now handle success with step 7 (thank you page) instead of immediately closing
  // useEffect(() => {
  //   if (subscriptionCreated) {
  //     console.log('subscriptionCreated is true, resetting wizard');
  //     // Reset wizard and close modal
  //     setStep(1);
  //     setWizardData({
  //       metal_type: '',
  //       target_weight: 0,
  //       weight_type: '',
  //       period: '',
  //       amount_per_period: 0
  //     });
  //     onHide();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [subscriptionCreated]);
  
  const handleNext = () => {
    setStep(step + 1);
  };
  
  const handleBack = () => {
    setStep(step - 1);
  };
  
  const handleReviewComplete = () => {
    // Always go to payment method selection step
    // The payment method selection component will handle the logic
    console.log('handleReviewComplete called, setting step to 6');
    setStep(6);
    console.log('Step set to 6');
  };
  
  const handleCreateSubscription = async () => {
    setIsLoading(true);
    try {
      // Create subscription with target-based data
      const response = await api.createSubscription({
        metal_type: wizardData.metal_type,
        period: wizardData.period,
        amount_per_period: wizardData.amount_per_period,
        weight: wizardData.target_weight,
        weight_type: wizardData.weight_type
      });
      
      // Show success message
      setToastVariant('success');
      setToastMessage('Subscription created successfully!');
      setShowToast(true);
      
      // Refresh subscription list
      setTimeout(() => {
        dispatch(fetchSubscriptions());
      }, 500);
      
      // Reset wizard and close modal
      setTimeout(() => {
        setStep(1);
        setWizardData({
          metal_type: '',
          target_weight: 0,
          weight_type: '',
          period: '',
          amount_per_period: 0
        });
        onHide();
      }, 1500);
    } catch (error) {
      console.error('Error creating subscription:', error);
      
      setToastVariant('danger');
      setToastMessage(
        error.response?.data?.error || 
        error.message || 
        'Failed to create subscription. Please try again.'
      );
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check if user has saved payment methods
  const hasPaymentMethods = paymentMethods.length > 0;
  
  // Determine if this is the user's first subscription
  const subscriptions = useSelector(state => state.subscriptions?.subscriptions || []);
  const isFirstSubscription = subscriptions.length === 0;
  
  const handlePaymentMethodSelected = async (paymentMethodId) => {
    // This function is called from WizardStepPaymentMethodSelection for both cases:
    // 1. When user selects existing payment method (paymentMethodId will be undefined, get from wizardData)
    // 2. When user adds new payment method (paymentMethodId will be the new payment method ID)

    if (paymentMethodId) {
      // New payment method was added - update wizard data
      setWizardData(prev => ({
        ...prev,
        selected_payment_method_id: paymentMethodId
      }));
    }

    // Go to address selection step
    setStep(7);
  };

  const handleAddressSelected = async () => {
    // Called when user selects/adds an address and continues
    // Now create the subscription with payment method and address
    setIsLoading(true);
    try {
      const subscriptionData = {
        metal_type: wizardData.metal_type,
        amount_per_period: wizardData.amount_per_period,
        weight_type: wizardData.weight_type,
        period: wizardData.period,
        target_weight: wizardData.target_weight,
        selected_payment_method_id: wizardData.selected_payment_method_id,
        selected_address_id: wizardData.selected_address_id
      };

      const response = await api.createSubscriptionWithPaymentMethod(subscriptionData);

      if (response.data.success) {
        // Don't fetch subscriptions here - it causes the wizard to unmount
        // Dashboard will refresh subscriptions when the wizard closes

        // Go to thank you step
        setStep(8);
      } else {
        throw new Error(response.data.error || 'Failed to create goal');
      }

    } catch (error) {
      console.error('Error creating goal:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create goal';
      setToastMessage(errorMessage);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };


  const resetWizard = () => {
    setStep(1);
    setWizardData({
      metal_type: '',
      target_weight: 0,
      weight_type: '',
      period: '',
      amount_per_period: 0,
      selected_payment_method_id: null,
      selected_address_id: null
    });
    onHide();
  };

  const renderStep = () => {
    console.log('renderStep called with step:', step);
    switch (step) {
      case 1:
        return (
          <WizardStepMetalType 
            onNext={handleNext} 
            setWizardData={setWizardData} 
            wizardData={wizardData} 
          />
        );
      case 2:
        return (
          <WizardStepTargetWeight
            onNext={handleNext}
            onBack={handleBack}
            setWizardData={setWizardData}
            wizardData={wizardData}
          />
        );
      case 3:
        return (
          <WizardStepTimePeriod 
            onNext={handleNext} 
            onBack={handleBack} 
            setWizardData={setWizardData} 
            wizardData={wizardData} 
          />
        );
      case 4:
        return (
          <WizardStepAmount 
            onNext={handleNext} 
            onBack={handleBack} 
            setWizardData={setWizardData} 
            wizardData={wizardData} 
          />
        );
      case 5:
        return (
          <WizardStepReview 
            onBack={handleBack} 
            onAddToCart={handleReviewComplete}
            isLoading={isLoading}
            wizardData={wizardData} 
            buttonText="Continue to Payment"
          />
        );
      case 6:
        return (
          <WizardStepPaymentMethodSelection
            onNext={handlePaymentMethodSelected}
            onBack={handleBack}
            paymentMethods={paymentMethods}
            wizardData={wizardData}
            setWizardData={setWizardData}
          />
        );
      case 7:
        return (
          <WizardStepAddress
            onNext={handleAddressSelected}
            onBack={handleBack}
            wizardData={wizardData}
            setWizardData={setWizardData}
          />
        );
      case 8:
        return (
          <WizardStepThankYou
            onComplete={resetWizard}
            wizardData={wizardData}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <>
      <Modal
        show={show}
        onHide={step === 8 ? null : onHide}
        size={step === 5 ? "xl" : step === 8 ? "sm" : "lg"}
        centered
        backdrop="static"
        dialogClassName={step === 5 ? "modal-90w" : step === 8 ? "modal-success" : ""}
      >
        {step !== 8 && (
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1.25rem' }}>
              Create New Goal
            </Modal.Title>
          </Modal.Header>
        )}
        <Modal.Body className="p-0">
          {renderStep()}
        </Modal.Body>
      </Modal>
      
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          delay={3000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Success' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'success' ? '' : 'text-white'}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default React.memo(SubscriptionWizard);
